import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Crosshair,
  Loader2,
  LogIn,
  LogOut,
  MapPin,
  Pencil,
  RefreshCw,
  Save,
  ShieldCheck,
  X,
  Users
} from 'lucide-react';
import {
  attendanceService,
  AttendanceFilters,
  AttendanceRecord,
  AttendanceSummary,
  AttendanceUser,
  OfficeAssignmentRecord,
  OfficeInput,
  OfficeRecord
} from '../services/attendanceService';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window {
    google?: any;
    __amapGoogleMapsLoader?: Promise<any>;
  }
}

const emptyOfficeForm: OfficeInput = {
  officeName: '',
  officeEmail: '',
  officePhone: '',
  area: '',
  city: '',
  state: '',
  address: '',
  latitude: 0,
  longitude: 0,
  allowedRadiusMeters: 50,
  active: true
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatDistance = (value?: number | null) => {
  if (value == null) return '--';
  return `${Math.round(value)} m`;
};

const formatAttendanceStatus = (record: AttendanceRecord) => {
  if (record.checkInStatus === 'denied') return 'Denied';
  if (record.checkOutTime) return 'Checked out';
  if (record.checkInTime) return 'Present';
  return 'Pending';
};

const getFriendlyUiError = (message: string) => {
  if (message.includes('Attendance setup is incomplete')) {
    return `${message} Run the attendance SQL setup before using this section fully.`;
  }

  if (message.includes('schema is incomplete')) {
    return `${message} Update the Supabase schema, then refresh this page.`;
  }

  if (message.includes('No check-in record found for today')) {
    return 'You need to check in first before checking out.';
  }

  if (message.includes('outside office radius')) {
    return message;
  }

  return message;
};

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const defaultOfficeCenter = { lat: 28.6139, lng: 77.209 };

const loadGoogleMaps = () => {
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (window.__amapGoogleMapsLoader) {
    return window.__amapGoogleMapsLoader;
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error('Google Maps API key is not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file.'));
  }

  console.debug('[Maps] Loading with key:', GOOGLE_MAPS_API_KEY.slice(0, 12) + '…');

  window.__amapGoogleMapsLoader = new Promise((resolve, reject) => {
    // gm_authFailure fires when the key is invalid, billing is off, or the
    // Maps JavaScript API is not enabled for this project.
    const prevAuthFailure = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => {
      if (prevAuthFailure) prevAuthFailure();
      window.__amapGoogleMapsLoader = null;
      reject(new Error(
        'Google Maps authentication failed. ' +
        'Make sure the Maps JavaScript API is enabled in your Google Cloud project ' +
        'and that billing is set up for key: ' + GOOGLE_MAPS_API_KEY.slice(0, 8) + '…'
      ));
    };

    const existingScript = document.querySelector('script[data-google-maps="attendance"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps script.')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = 'attendance';
    script.onload = () => resolve(window.google);
    script.onerror = () => {
      window.__amapGoogleMapsLoader = null;
      reject(new Error('Failed to load Google Maps script. Check your network connection.'));
    };
    document.head.appendChild(script);
  });

  return window.__amapGoogleMapsLoader;
};

const OfficeLocationPicker: React.FC<{
  officeForm: OfficeInput;
  setOfficeForm: React.Dispatch<React.SetStateAction<OfficeInput>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}> = ({ officeForm, setOfficeForm, setError }) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const autocompleteRef = useRef<any>(null);
  const updatingFromMapRef = useRef(false);
  const [mapsError, setMapsError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    loadGoogleMaps()
      .then((google) => {
        if (!mounted || !mapRef.current || !addressInputRef.current) {
          return;
        }

        const initialCenter =
          officeForm.latitude && officeForm.longitude
            ? { lat: officeForm.latitude, lng: officeForm.longitude }
            : defaultOfficeCenter;

        geocoderRef.current = new google.maps.Geocoder();
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: initialCenter,
          zoom: officeForm.latitude && officeForm.longitude ? 18 : 16,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false
        });

        const reverseGeocode = (lat: number, lng: number) => {
          if (!geocoderRef.current) {
            return;
          }

          geocoderRef.current.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
            if (status !== 'OK' || !results?.[0]) {
              setError('Unable to find address for the selected office point.');
              return;
            }

            const place = results[0];
            let area = '';
            let city = '';
            let state = '';

            place.address_components.forEach((component: any) => {
              if (component.types.includes('sublocality') || component.types.includes('sublocality_level_1')) {
                area = component.long_name;
              }
              if (component.types.includes('locality')) {
                city = component.long_name;
              }
              if (component.types.includes('administrative_area_level_1')) {
                state = component.long_name;
              }
            });

            updatingFromMapRef.current = true;
            setOfficeForm((current) => ({
              ...current,
              address: place.formatted_address || current.address,
              area,
              city,
              state,
              latitude: Number(lat.toFixed(7)),
              longitude: Number(lng.toFixed(7))
            }));
            setError(null);
            window.setTimeout(() => {
              updatingFromMapRef.current = false;
            }, 0);
          });
        };

        mapInstanceRef.current.addListener('idle', () => {
          const center = mapInstanceRef.current?.getCenter?.();
          if (!center) {
            return;
          }
          reverseGeocode(center.lat(), center.lng());
        });

        autocompleteRef.current = new google.maps.places.Autocomplete(addressInputRef.current, {
          types: ['geocode'],
          componentRestrictions: { country: 'IN' }
        });

        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace?.();
          if (!place || !place.geometry || !place.geometry.location) {
            setError('Please select a valid address from the map suggestions.');
            return;
          }
          const lat = place.geometry?.location?.lat?.();
          const lng = place.geometry?.location?.lng?.();

          if (typeof lat !== 'number' || typeof lng !== 'number') {
            setError('Please select a valid address from the map suggestions.');
            return;
          }

          mapInstanceRef.current?.setCenter({ lat, lng });
          mapInstanceRef.current?.setZoom(18);
        });
      })
      .catch((mapError: any) => {
        if (mounted) {
          setMapsError(mapError?.message || 'Failed to initialize Google Maps.');
        }
      });

    return () => {
      mounted = false;
    };
  }, [officeForm.latitude, officeForm.longitude, setError, setOfficeForm]);

  useEffect(() => {
    if (!mapInstanceRef.current || updatingFromMapRef.current) {
      return;
    }

    if (!officeForm.latitude || !officeForm.longitude) {
      return;
    }

    mapInstanceRef.current.setCenter({
      lat: officeForm.latitude,
      lng: officeForm.longitude
    });
  }, [officeForm.latitude, officeForm.longitude]);

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">Office Full Address</label>
        <input
          ref={addressInputRef}
          value={officeForm.address}
          onChange={(event) => setOfficeForm((current) => ({ ...current, address: event.target.value }))}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Search or drag the map to set office location"
          required
        />
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
            <MapPin className="h-4 w-4" />
            Select Office Location
          </div>
          <span className="text-xs text-blue-700">Drag map, keep pin at center</span>
        </div>

        <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-white">
          {mapsError ? (
            <div className="flex h-[360px] w-full flex-col items-center justify-center gap-3 bg-gray-50 p-6 text-center">
              <MapPin className="h-10 w-10 text-gray-300" />
              <p className="text-sm font-medium text-red-600">Map unavailable</p>
              <p className="max-w-sm text-xs text-gray-500">{mapsError}</p>
              <p className="max-w-sm text-xs text-gray-400">
                To fix: enable the <strong>Maps JavaScript API</strong> in your{' '}
                <span className="font-mono">Google Cloud Console</span> and ensure billing is active for your project.
              </p>
            </div>
          ) : (
            <>
              <div ref={mapRef} className="h-[360px] w-full" />
              <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-full">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white shadow-lg">
                  <MapPin className="h-5 w-5" />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Area</label>
            <input
              type="text"
              value={officeForm.area}
              readOnly
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-700"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">City</label>
            <input
              type="text"
              value={officeForm.city}
              readOnly
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-700"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">State</label>
            <input
              type="text"
              value={officeForm.state}
              readOnly
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-gray-700"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Allowed Radius (m)</label>
            <input
              type="number"
              min="1"
              required
              value={officeForm.allowedRadiusMeters || 50}
              onChange={(event) => setOfficeForm((current) => ({ ...current, allowedRadiusMeters: Number(event.target.value) }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const AttendanceManagement: React.FC = () => {
  const { currentUser, adminProfile, isSuperAdmin } = useAuth();
  const [summary, setSummary] = useState<AttendanceSummary | null>(null);
  const [offices, setOffices] = useState<OfficeRecord[]>([]);
  const [users, setUsers] = useState<AttendanceUser[]>([]);
  const [assignments, setAssignments] = useState<OfficeAssignmentRecord[]>([]);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingOffice, setSavingOffice] = useState(false);
  const [assigningUserId, setAssigningUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingOfficeId, setEditingOfficeId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'office' | 'report' | 'actions'>('actions');
  const [officeForm, setOfficeForm] = useState<OfficeInput>(emptyOfficeForm);
  const hasLoadedRef = useRef(false);
  const [selectedActionUserId, setSelectedActionUserId] = useState('');
  const [selectedActionOfficeId, setSelectedActionOfficeId] = useState('');
  const [actionLocation, setActionLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [actionLoading, setActionLoading] = useState<'checkin' | 'checkout' | null>(null);
  const [locationPermission, setLocationPermission] = useState<'granted' | 'denied' | 'prompt' | 'unknown' | 'insecure'>('unknown');
  const [actionSuccessModal, setActionSuccessModal] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({
    open: false,
    title: '',
    message: ''
  });
  const [filters, setFilters] = useState<AttendanceFilters>({
    status: 'all',
    fromDate: '',
    toDate: ''
  });

  const currentActorId = currentUser?.uid || adminProfile?.uid || '';

  // Refs so loadAll can always read the latest values without being recreated on every change.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const currentActorIdRef = useRef(currentActorId);
  currentActorIdRef.current = currentActorId;
  const isSuperAdminRef = useRef(isSuperAdmin);
  isSuperAdminRef.current = isSuperAdmin;
  const currentActorKeys = useMemo(
    () =>
      Array.from(
        new Set(
          [currentUser?.uid, adminProfile?.uid, adminProfile?.adminDocId].filter(
            (value): value is string => Boolean(value)
          )
        )
      ),
    [adminProfile?.adminDocId, adminProfile?.uid, currentUser?.uid]
  );

  const loadAll = useCallback(async (withSpinner = true) => {
    const lFilters = filtersRef.current;
    const lCurrentActorId = currentActorIdRef.current;
    const lIsSuperAdmin = isSuperAdminRef.current;

    try {
      if (withSpinner) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError(null);
      const results = await Promise.allSettled([
        attendanceService.getAttendanceSummary(),
        attendanceService.getOffices(),
        attendanceService.getAssignableUsers(),
        attendanceService.getOfficeAssignments(),
        attendanceService.getAttendanceRecords(
          lIsSuperAdmin
            ? lFilters
            : {
                ...lFilters,
                userId: lCurrentActorId || undefined
              }
        )
      ]);

      const summaryResult = results[0];
      const officesResult = results[1];
      const usersResult = results[2];
      const assignmentsResult = results[3];
      const recordsResult = results[4];

      if (officesResult.status === 'fulfilled') {
        setOffices(officesResult.value);
      } else {
        throw officesResult.reason;
      }

      if (summaryResult.status === 'fulfilled') {
        setSummary(summaryResult.value);
      } else {
        setSummary({
          totalOffices: officesResult.value.length,
          activeAssignments: 0,
          todayPresent: 0,
          todayDenied: 0
        });
      }

      if (usersResult.status === 'fulfilled') {
        setUsers(usersResult.value);
      } else {
        setUsers([]);
      }

      if (assignmentsResult.status === 'fulfilled') {
        setAssignments(assignmentsResult.value);
      } else {
        setAssignments([]);
      }

      if (recordsResult.status === 'fulfilled') {
        setRecords(
          lIsSuperAdmin
            ? recordsResult.value
            : recordsResult.value.filter(
                (record) =>
                  record.userUid === lCurrentActorId ||
                  record.userId === lCurrentActorId ||
                  record.user?.uid === lCurrentActorId
              )
        );
      } else {
        setRecords([]);
      }

      const optionalFailures = [summaryResult, usersResult, assignmentsResult, recordsResult]
        .filter((result) => result.status === 'rejected')
        .map((result) => (result as PromiseRejectedResult).reason?.message || 'Unknown error');

      if (optionalFailures.length > 0) {
      console.log('Optional data load failures:', optionalFailures);
       // setError(`Attendance setup is incomplete. Offices are loaded, but some sections are unavailable: ${optionalFailures[0]}`);
      }
    } catch (serviceError: any) {
      setError(getFriendlyUiError(serviceError?.message || 'Failed to load attendance module.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run when the authenticated user or their admin role changes (e.g. first load after auth resolves).
  useEffect(() => {
    loadAll(!hasLoadedRef.current).finally(() => {
      hasLoadedRef.current = true;
    });
    // loadAll is stable (empty deps), so this only fires when currentActorId / isSuperAdmin change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActorId, isSuperAdmin]);

  // Re-run only when the user explicitly changes a report filter (not on initial mount).
  const hasAppliedInitialFilters = useRef(false);
  useEffect(() => {
    if (!hasAppliedInitialFilters.current) {
      hasAppliedInitialFilters.current = true;
      return;
    }
    loadAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const resetOfficeForm = () => {
    setEditingOfficeId(null);
    setOfficeForm(emptyOfficeForm);
  };

  const handleSaveOffice = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingOffice(true);
    setError(null);
    setSuccess(null);

    try {
      await attendanceService.saveOffice(officeForm, editingOfficeId || undefined);
      setSuccess(editingOfficeId ? 'Office updated successfully.' : 'Office created successfully.');
      resetOfficeForm();
      await loadAll(false);
    } catch (serviceError: any) {
      setError(getFriendlyUiError(serviceError?.message || 'Failed to save office.'));
    } finally {
      setSavingOffice(false);
    }
  };

  const handleEditOffice = (office: OfficeRecord) => {
    setEditingOfficeId(office.id);
    setOfficeForm({
      officeName: office.officeName,
      officeEmail: office.officeEmail,
      officePhone: office.officePhone,
      area: office.area,
      city: office.city,
      state: office.state,
      address: office.address,
      latitude: office.latitude,
      longitude: office.longitude,
      allowedRadiusMeters: office.allowedRadiusMeters,
      active: office.active
    });
    setSuccess(null);
    setError(null);
  };

  const populateCurrentCoordinates = () => {
    setError(null);

    if (!navigator.geolocation) {
      setError('Browser geolocation is not available on this device.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setOfficeForm((current) => ({
          ...current,
          latitude: Number(position.coords.latitude.toFixed(7)),
          longitude: Number(position.coords.longitude.toFixed(7))
        }));
      },
      (geoError) => {
        setError(geoError.message || 'Unable to fetch current coordinates.');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleAssignOffice = async (userId: string, officeId: string) => {
    if (!officeId) {
      return;
    }

    setAssigningUserId(userId);
    setError(null);
    setSuccess(null);

    try {
      await attendanceService.assignUserToOffice(userId, officeId);
      setSuccess('Office assignment updated.');
      await loadAll(false);
    } catch (serviceError: any) {
      setError(getFriendlyUiError(serviceError?.message || 'Failed to update office assignment.'));
    } finally {
      setAssigningUserId(null);
    }
  };

  const assignmentMap = useMemo(
    () => new Map(assignments.map((assignment) => [assignment.userId, assignment])),
    [assignments]
  );
  const currentActorAssignment = useMemo(
    () => currentActorKeys.map((key) => assignmentMap.get(key)).find(Boolean),
    [assignmentMap, currentActorKeys]
  );
  const userLookup = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users]
  );

  useEffect(() => {
    if (!currentActorId) {
      return;
    }

    if (!isSuperAdmin) {
      setSelectedActionUserId(currentActorId);
      const assignedOfficeId = currentActorAssignment?.officeId || '';
      setSelectedActionOfficeId(assignedOfficeId);
      if (activeTab === 'office') {
        setActiveTab('actions');
      }
    }
  }, [activeTab, currentActorAssignment, currentActorId, isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && activeTab === 'actions' && !hasLoadedRef.current) {
      setActiveTab('office');
    }
  }, [activeTab, isSuperAdmin]);

  // Check (and subscribe to changes of) the browser geolocation permission whenever the
  // Actions tab is visible, so we can warn the user before they try to check in.
  useEffect(() => {
    if (activeTab !== 'actions') return;

    // Geolocation is blocked on HTTP (non-localhost). Detect this first so we can
    // show a clear "needs HTTPS" message instead of a misleading "access denied" one.
    if (!window.isSecureContext) {
      setLocationPermission('insecure');
      return;
    }

    if (!navigator.permissions) {
      setLocationPermission('unknown');
      return;
    }
    let permissionStatus: PermissionStatus | null = null;
    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      permissionStatus = status;
      setLocationPermission(status.state as 'granted' | 'denied' | 'prompt');
      status.onchange = () => {
        setLocationPermission(status.state as 'granted' | 'denied' | 'prompt');
      };
    }).catch(() => {
      setLocationPermission('unknown');
    });
    return () => {
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, [activeTab]);

  const requestLocationPermission = useCallback(() => {
    if (!window.isSecureContext) {
      setLocationPermission('insecure');
      return;
    }
    if (!navigator.geolocation) {
      setLocationPermission('denied');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationPermission('granted');
        setActionLocation({
          latitude: Number(position.coords.latitude.toFixed(7)),
          longitude: Number(position.coords.longitude.toFixed(7))
        });
      },
      (geoError) => {
        // Any failure here means the check-in will also fail — reflect that in the UI.
        if (geoError.code === geoError.PERMISSION_DENIED) {
          setLocationPermission('denied');
        } else {
          // POSITION_UNAVAILABLE or TIMEOUT: permission may be granted but location
          // can't be resolved right now. Reset to 'unknown' so the user can retry.
          setLocationPermission('unknown');
        }
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, []);

  const captureCurrentLocation = () =>
    new Promise<{ latitude: number; longitude: number }>((resolve, reject) => {
      if (!window.isSecureContext) {
        reject(new Error('Geolocation requires a secure connection (HTTPS). Ask your administrator to enable HTTPS for this site.'));
        return;
      }
      if (!navigator.geolocation) {
        reject(new Error('Browser geolocation is not available on this device.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: Number(position.coords.latitude.toFixed(7)),
            longitude: Number(position.coords.longitude.toFixed(7))
          }),
        (geoError) => {
          let message: string;
          if (geoError.code === geoError.PERMISSION_DENIED) {
            // Keep the UI permission indicator in sync — the Permissions API can
            // report "granted" while the OS-level location service is off, which
            // also produces a PERMISSION_DENIED code at the API level.
            setLocationPermission('denied');
            message =
              'Location access was denied. Please check: (1) Browser — allow location for this site in your browser settings, (2) System — enable location services for your browser in your OS privacy settings (Windows: Settings → Privacy → Location).';
          } else if (geoError.code === geoError.POSITION_UNAVAILABLE) {
            message = 'Your location could not be determined. Check your GPS or network connection and try again.';
          } else if (geoError.code === geoError.TIMEOUT) {
            message = 'Location request timed out. Please try again.';
          } else {
            message = geoError.message || 'Unable to fetch current coordinates.';
          }
          reject(new Error(message));
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });

  const handleAttendanceAction = async (type: 'checkin' | 'checkout') => {
    const effectiveUserId = isSuperAdmin ? selectedActionUserId : currentActorId;

    if (!effectiveUserId || !selectedActionOfficeId) {
      setError('Select both user and office first.');
      return;
    }

    setActionLoading(type);
    setError(null);
    setSuccess(null);

    try {
      const location = await captureCurrentLocation();
      setActionLocation(location);

      if (type === 'checkin') {
        const record = await attendanceService.recordCheckIn({
          userId: effectiveUserId,
          officeId: selectedActionOfficeId,
          latitude: location.latitude,
          longitude: location.longitude,
          locationPermissionStatus: 'while_in_use',
          source: 'admin_web'
        });
        setSuccess(
          record.checkInStatus === 'denied'
            ? `Check-in denied. User is outside office radius at ${Math.round(record.checkInDistanceMeters || 0)}m.`
            : 'Check-in recorded successfully.'
        );
        if (record.checkInStatus !== 'denied') {
          setActionSuccessModal({
            open: true,
            title: 'Check-in Successful',
            message: 'Your attendance has been marked successfully.'
          });
        }
      } else {
        const record = await attendanceService.recordCheckOut({
          userId: effectiveUserId,
          officeId: selectedActionOfficeId,
          latitude: location.latitude,
          longitude: location.longitude,
          source: 'admin_web'
        });
        setSuccess(
          record.checkOutStatus === 'denied'
            ? `Check-out recorded outside office radius at ${Math.round(record.checkOutDistanceMeters || 0)}m.`
            : 'Check-out recorded successfully.'
        );
        if (record.checkOutStatus !== 'denied') {
          setActionSuccessModal({
            open: true,
            title: 'Check-out Successful',
            message: 'Your check-out has been recorded successfully.'
          });
        }
      }

      await loadAll(false);
    } catch (serviceError: any) {
      setError(getFriendlyUiError(serviceError?.message || `Failed to ${type}.`));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {actionSuccessModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-green-100 p-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{actionSuccessModal.title}</h3>
                  <p className="mt-1 text-sm text-gray-600">{actionSuccessModal.message}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActionSuccessModal({ open: false, title: '', message: '' })}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setActionSuccessModal({ open: false, title: '', message: '' })}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border-b border-gray-200 pb-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Attendance Management</h1>
            <p className="mt-1 text-gray-600">
              Same office model as the legacy system: office coordinates, a 50 meter geofence, and late or early attendance flags.
            </p>
          </div>
          <button
            type="button"
            onClick={() => loadAll(false)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
          {success}
        </div>
      )}

      {isSuperAdmin ? (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Offices</p>
              <p className="text-3xl font-semibold text-gray-900">{summary?.totalOffices || 0}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Office Assignments</p>
              <p className="text-3xl font-semibold text-gray-900">{summary?.activeAssignments || 0}</p>
            </div>
            <Users className="h-8 w-8 text-emerald-600" />
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Present Today</p>
              <p className="text-3xl font-semibold text-gray-900">{summary?.todayPresent || 0}</p>
            </div>
            <ClipboardCheck className="h-8 w-8 text-indigo-600" />
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Denied Today</p>
              <p className="text-3xl font-semibold text-gray-900">{summary?.todayDenied || 0}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
        </div>
      </div>
      ) : (
      <div className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-600 shadow-sm">
        Attendance access is limited to your own check-in, check-out, and personal attendance records.
      </div>
      )}

      <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => setActiveTab('office')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'office' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            1. Office Management
          </button>
        )}
        <button
          type="button"
          onClick={() => setActiveTab('report')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'report' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {isSuperAdmin ? '2. View Attendance Report' : '1. My Attendance'}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('actions')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'actions' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {isSuperAdmin ? '3. Check In / Check Out' : '2. Check In / Check Out'}
        </button>
      </div>

      {isSuperAdmin && activeTab === 'office' && (
      <>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr,1.05fr]">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {editingOfficeId ? 'Edit Office' : 'Create Office'}
              </h2>
              <p className="text-sm text-gray-500">
                Match the legacy office schema with address plus latitude and longitude for geofenced attendance.
              </p>
            </div>
            {editingOfficeId && (
              <button
                type="button"
                onClick={resetOfficeForm}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel Edit
              </button>
            )}
          </div>

          <form className="space-y-4" onSubmit={handleSaveOffice}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Office Name</label>
                <input
                  type="text"
                  required
                  value={officeForm.officeName}
                  onChange={(event) => setOfficeForm((current) => ({ ...current, officeName: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Mumbai Head Office"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Office Email</label>
                <input
                  type="email"
                  value={officeForm.officeEmail}
                  onChange={(event) => setOfficeForm((current) => ({ ...current, officeEmail: event.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="office@company.com"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Office Phone</label>
              <input
                type="text"
                value={officeForm.officePhone}
                onChange={(event) => setOfficeForm((current) => ({ ...current, officePhone: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+91 98765 43210"
              />
            </div>

            <button
              type="button"
              onClick={populateCurrentCoordinates}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
            >
              <Crosshair className="h-4 w-4" />
              Center Map On Current Location
            </button>

            <OfficeLocationPicker officeForm={officeForm} setOfficeForm={setOfficeForm} setError={setError} />

            <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={officeForm.active !== false}
                onChange={(event) => setOfficeForm((current) => ({ ...current, active: event.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Office is active for attendance
            </label>

            <button
              type="submit"
              disabled={savingOffice}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {savingOffice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {savingOffice ? 'Saving Office...' : editingOfficeId ? 'Save Office Changes' : 'Create Office'}
            </button>
          </form>
        </div>
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center gap-3">
              <div className="rounded-full bg-emerald-100 p-2">
                <ShieldCheck className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Geofence Policy</h2>
                <p className="text-sm text-gray-500">Legacy attendance rules mirrored in this dashboard.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-500">Geofence</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">50 meters</p>
                <p className="mt-1 text-xs text-gray-500">Check-in is denied outside the office radius.</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-500">Late Mark</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">After 08:45 AM</p>
                <p className="mt-1 text-xs text-gray-500">Same threshold as the legacy mobile attendance API.</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-500">Early Mark</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">07:00 AM to 08:30 AM</p>
                <p className="mt-1 text-xs text-gray-500">Attendance records are tagged early in this window.</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Configured Offices</h2>
              <p className="text-sm text-gray-500">Review and edit office coordinates, radius, and status.</p>
            </div>

            <div className="space-y-3">
              {offices.map((office) => (
                <div key={office.id} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-900">{office.officeName}</h3>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${office.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {office.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">{office.address}</p>
                      <p className="mt-2 text-xs text-gray-500">
                        {office.city}, {office.state} | Radius: {office.allowedRadiusMeters}m | Lat: {office.latitude}, Lng: {office.longitude}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleEditOffice(office)}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </button>
                  </div>
                </div>
              ))}

              {offices.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                  No offices configured yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-3">
          <div className="rounded-full bg-indigo-100 p-2">
            <Users className="h-5 w-5 text-indigo-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Office Assignment</h2>
            <p className="text-sm text-gray-500">Bind each user to a single office so attendance can validate against that office geofence.</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">User</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Location</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Current Office</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Assign Office</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {users.map((user) => {
                const assignment = assignmentMap.get(user.id);
                return (
                  <tr key={user.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{user.displayName}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {[user.city, user.state].filter(Boolean).join(', ') || user.location || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {assignment?.office?.officeName || 'Not assigned'}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={assignment?.officeId || ''}
                        onChange={(event) => handleAssignOffice(user.id, event.target.value)}
                        disabled={assigningUserId === user.id}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select office</option>
                        {offices
                          .filter((office) => office.active)
                          .map((office) => (
                            <option key={office.id} value={office.id}>
                              {office.officeName}
                            </option>
                          ))}
                      </select>
                    </td>
                  </tr>
                );
              })}

              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    No users found for office assignment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {activeTab === 'report' && (
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{isSuperAdmin ? 'Attendance Monitor' : 'My Attendance'}</h2>
            <p className="text-sm text-gray-500">
              {isSuperAdmin
                ? 'Track successful, denied, late, and early attendance against the office geofence.'
                : 'Review only your own attendance records.'}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            {isSuperAdmin && (
              <select
                value={filters.officeId || ''}
                onChange={(event) => setFilters((current) => ({ ...current, officeId: event.target.value || undefined }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All offices</option>
                {offices.map((office) => (
                  <option key={office.id} value={office.id}>
                    {office.officeName}
                  </option>
                ))}
              </select>
            )}

            {isSuperAdmin && (
              <select
                value={filters.userId || ''}
                onChange={(event) => setFilters((current) => ({ ...current, userId: event.target.value || undefined }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All users</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
              </select>
            )}

            <select
              value={filters.status || 'all'}
              onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value as AttendanceFilters['status'] }))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All statuses</option>
              <option value="success">Success</option>
              <option value="denied">Denied</option>
            </select>

            <input
              type="date"
              value={filters.fromDate || ''}
              onChange={(event) => setFilters((current) => ({ ...current, fromDate: event.target.value }))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <input
              type="date"
              value={filters.toDate || ''}
              onChange={(event) => setFilters((current) => ({ ...current, toDate: event.target.value }))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">User</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Office</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Check In</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Check Out</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Distance</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Flags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {records.map((record) => {
                const resolvedUser = record.user || (record.userUid ? userLookup.get(record.userUid) : undefined);
                return (
                <tr key={record.id}>
                  <td className="px-4 py-3 text-gray-700">{record.attendanceDate}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{resolvedUser?.displayName || 'Unknown user'}</div>
                    <div className="text-xs text-gray-500">{resolvedUser?.email || 'No email'}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{record.office?.officeName || 'Unknown office'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                      record.checkInStatus === 'denied'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {formatAttendanceStatus(record)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{formatDateTime(record.checkInTime)}</td>
                  <td className="px-4 py-3 text-gray-700">{formatDateTime(record.checkOutTime)}</td>
                  <td className="px-4 py-3 text-gray-700">{formatDistance(record.checkInDistanceMeters)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {record.checkInStatus === 'success' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          In range
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Outside radius
                        </span>
                      )}
                      {record.isLate && (
                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                          Late
                        </span>
                      )}
                      {record.isEarly && (
                        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                          Early
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}

              {records.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No attendance records found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {activeTab === 'actions' && (
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.8fr,1.2fr]">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-gray-900">{isSuperAdmin ? 'Check In / Check Out' : 'My Check In / Check Out'}</h2>
            <p className="text-sm text-gray-500">Use the current browser geolocation against the selected office geofence.</p>
          </div>

          <div className="space-y-4">
            {isSuperAdmin ? (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">User</label>
                <select
                  value={selectedActionUserId}
                  onChange={(event) => {
                    const userId = event.target.value;
                    const assignedOfficeId = assignmentMap.get(userId)?.officeId || '';
                    setSelectedActionUserId(userId);
                    setSelectedActionOfficeId(assignedOfficeId);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select user</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-500">Logged In User</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">{adminProfile?.displayName || currentUser?.email || 'Current user'}</p>
                <p className="mt-1 text-xs text-gray-500">{adminProfile?.email || currentUser?.email || ''}</p>
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Office</label>
              <select
                value={selectedActionOfficeId}
                onChange={(event) => setSelectedActionOfficeId(event.target.value)}
                disabled={!isSuperAdmin}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select office</option>
                {offices.filter((office) => office.active).map((office) => (
                  <option key={office.id} value={office.id}>
                    {office.officeName}
                  </option>
                ))}
              </select>
              {!isSuperAdmin && (
                <p className="mt-1 text-xs text-gray-500">Only your assigned office can be used for attendance actions.</p>
              )}
            </div>

            {locationPermission === 'insecure' ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                <p className="font-medium text-amber-800">HTTPS required for location</p>
                <p className="mt-1 text-xs text-amber-700">
                  Browsers block location access on HTTP. The site must be served over HTTPS for check-in to work.
                </p>
              </div>
            ) : locationPermission === 'denied' ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
                <p className="font-medium text-red-700">Location access is blocked</p>
                <p className="mt-1 text-xs text-red-600">Check both:</p>
                <ol className="mt-1 list-decimal pl-4 text-xs text-red-600 space-y-0.5">
                  <li>Browser — click the lock icon in the address bar and set Location to "Allow"</li>
                  <li>System — Windows: Settings → Privacy &amp; Security → Location → enable for your browser</li>
                </ol>
                <p className="mt-2 text-xs text-red-500">Refresh the page after changing either setting.</p>
              </div>
            ) : locationPermission === 'granted' ? (
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                Location access granted
              </div>
            ) : (
              <button
                type="button"
                onClick={requestLocationPermission}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
              >
                <MapPin className="h-4 w-4" />
                Allow Location Access
              </button>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <button
                type="button"
                onClick={() => handleAttendanceAction('checkin')}
                disabled={actionLoading !== null || (locationPermission !== 'granted' && locationPermission !== 'unknown')}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
              >
                {actionLoading === 'checkin' ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                Check In
              </button>
              <button
                type="button"
                onClick={() => handleAttendanceAction('checkout')}
                disabled={actionLoading !== null || (locationPermission !== 'granted' && locationPermission !== 'unknown')}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
              >
                {actionLoading === 'checkout' ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                Check Out
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900">Action Details</h3>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-500">Assigned Office</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">
                  {offices.find((office) => office.id === selectedActionOfficeId)?.officeName || 'Not selected'}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {offices.find((office) => office.id === selectedActionOfficeId)?.address || 'Pick a user or office to proceed.'}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-500">Current Browser Location</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">
                  {actionLocation ? `${actionLocation.latitude}, ${actionLocation.longitude}` : 'Not captured yet'}
                </p>
                <p className="mt-1 text-xs text-gray-500">Location is captured at the moment you press check in or check out.</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-base font-semibold text-gray-900">Latest Records</h3>
            <div className="mt-4 space-y-3">
              {records.slice(0, 5).map((record) => (
                <div key={record.id} className="rounded-lg border border-gray-200 p-4">
                  {(() => {
                    const resolvedUser = record.user || (record.userUid ? userLookup.get(record.userUid) : undefined);
                    return (
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{resolvedUser?.displayName || 'Unknown user'}</div>
                      <div className="text-xs text-gray-500">{record.office?.officeName || 'Unknown office'} | {record.attendanceDate}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        record.checkInStatus === 'denied' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        In: {record.checkInStatus || 'pending'}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        record.checkOutStatus === 'denied' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        Out: {record.checkOutStatus || 'pending'}
                      </span>
                    </div>
                  </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default AttendanceManagement;
